import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { add, differenceInDays, endOfToday, format, getHours, getMinutes, isAfter, isBefore, isFuture, isSameMinute, parse, parseISO, set, startOfDay, startOfToday, sub } from 'date-fns';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { CreateBookingModalComponent } from '../../shared/modals/create-booking-modal/create-booking-modal.component';
import { ChooseBookOrBlockModalComponent } from '../../shared/modals/choose-book-or-block-modal/choose-book-or-block-modal.component';
import { BehaviorSubject, Subject } from 'rxjs';
import { CreateHolidayModalComponent } from '../../shared/modals/create-holiday-modal/create-holiday-modal.component';
import { ConfirmDeleteRecurrenceModalComponent } from '../../shared/modals/confirm-delete-recurrence-modal/confirm-delete-recurrence-modal.component';
import { ConfirmDeleteMultipleModalComponent } from '../../shared/modals/confirm-delete-multiple-modal/confirm-delete-multiple-modal.component';
import { ConfirmModalComponent } from '../../shared/modals/confirm-modal/confirm-modal.component';
import { TranslateService } from '@ngx-translate/core';
import { StoresService } from '../../core/services/stores.service';
import { CreateInvoiceModalComponent } from '../../shared/modals/create-invoice-modal/create-invoice-modal.component';
import { environment } from '../../../environments/environment';
import { CalendarEvent } from 'angular-calendar';
import { ToolsService } from '../../core/services/tools.service';
import { ConfirmBookingDragModalComponent } from '../../shared/modals/confirm-booking-drag-modal/confirm-booking-drag-modal.component';
import { isPlatformBrowser } from '@angular/common';
import { ApiGatewayService } from '../../core/services/api-gateway.service';
import { UserService } from '../../core/services/user.service';
import { ToastNotificationsService } from '../../core/services/toast-notifications.service';

export enum IViewCalendarBy {
  PROVIDERS = 'providers',
  ROOMS = 'rooms'
}

export interface CalendarDay {
  day: string;
  isOpen: boolean;
  events: Array<{ type: 'booking' | 'holiday' | 'roomOccupancy'; data: any }>;
  workplanOverride?: unknown[];
}

@Injectable({ providedIn: 'root' })
export class CalendarService {

  public refreshCalendarObservable = new Subject<boolean>();
  public viewCalendarBy$ = new BehaviorSubject<IViewCalendarBy>(IViewCalendarBy.PROVIDERS);

  private bsModalRef: BsModalRef;
  private deleteModalRef: BsModalRef;
  private editModalCallback;
  private deletingBookingIds = new Set<number | string>();
  private deletingHolidayIds = new Set<number | string>();

  constructor(
    private api: ApiGatewayService,
    private modalService: BsModalService,
    private translate: TranslateService,
    private storeService: StoresService,
    private toolsService: ToolsService,
    private userService: UserService,
    private toastNotificationsService: ToastNotificationsService,
    @Inject(PLATFORM_ID) protected platformId,
  ) {}

  chooseBookOrBlock(merchant, store, user, slotDate = null, provider = null, roomEquipment = null) {
    const initialState = {
      store,
      user,
      provider,
      roomEquipment
    };

    this.bsModalRef = this.modalService.show(ChooseBookOrBlockModalComponent, { initialState, class: 'modal-options modal-sm' });

    const thisModalNumber = this.modalService.getModalsCount();

    const modalCallback = this.modalService.onHide.subscribe((reason: string) => {
      if (thisModalNumber - 1 === this.modalService.getModalsCount()) {
        // just run this code on this exact modal hide
        if (reason && reason === 'book') {
          this.createNewBooking(slotDate, merchant, store, provider, roomEquipment);
        } else if (reason && reason === 'block') {
          this.createNewHoliday(slotDate, merchant, store, provider);
        }
        modalCallback.unsubscribe();
      }
    });
  }

  createNewBooking(slotDate, merchant, store, provider = null, roomEquipment = null) {
    const initialState = {
      day: format(slotDate, 'yyyy-MM-dd'),
      store: store,
      merchant: merchant,
    };

    if (slotDate && format(slotDate, 'HH:mm') !== '00:00') {
      initialState['defaultStartHour'] = format(slotDate, 'HH');
      initialState['defaultStartMinutes'] = format(slotDate, 'mm');
    }

    if (provider) {
      initialState['defaultProviderId'] = provider.id;
    }
    if (roomEquipment) {
      initialState['defaultRoomEquipmentId'] = roomEquipment.id;
    }

    this.bsModalRef = this.modalService.show(CreateBookingModalComponent, { initialState, class: 'modal-xl' });

    const thisModalNumber = this.modalService.getModalsCount();

    const modalCallback = this.modalService.onHide.subscribe((reason: string) => {
      // this is because subscribe of onHide would trigger on 2nd modal open (overlay of modals)
      // TODO find if there's a better way to identify which modal triggered hide
      // feature request here: https://github.com/valor-software/ngx-bootstrap/issues/2917
      if (thisModalNumber - 1 === this.modalService.getModalsCount()) {
        // just run this code on this exact modal hide
        if (reason && reason === 'saved') {
          this.refreshCalendarObservable.next(true);
        }
        // means is the first booking in the first day
        // removed because it interferes with the setuptour complete modal
        // if (reason && reason === 'open-flash-campaign-modal') {

        //   const days = differenceInDays(new Date(), parseISO(merchant.createdAt))
        //   if (days ===  0) {
        //     setTimeout(() => {
        //       this.bsModalRef = this.modalService.show(PaymentFlashCampaignModalComponent, { class: 'modal-md' });
        //     }, 2000);
        //   }
        //   this.refreshCalendarObservable.next(true);
        // }
        modalCallback.unsubscribe();
      }
    });
  }

  createNewHoliday(slotDate, merchant, store, provider = null) {
      const initialState = {
        day: format(slotDate, 'yyyy-MM-dd'),
        storeSlug: store.slug,
      };
      if (slotDate && format(slotDate, 'HH:mm') !== '00:00') {
        initialState['defaultStartHour'] = format(slotDate, 'HH');
        initialState['defaultStartMinutes'] = format(slotDate, 'mm');
      }
      if (provider) {
        initialState['defaultProviderId'] = provider.id;
      }

      this.bsModalRef = this.modalService.show(CreateHolidayModalComponent, { initialState });
      const modalCallback = this.modalService.onHide.subscribe((reason: string) => {
        if (reason && reason === 'saved') {
          this.refreshCalendarObservable.next(true);
        }
        modalCallback.unsubscribe();
      });
  }

  getBooking(bookingId) {
    return this.api.get(`bookings/${bookingId}`);
  }

  editBooking(merchant, store, eventMeta, viewDate?, options?: { modalClass?: string; fetchFullBooking?: boolean }) {
    const booking = eventMeta?.data;
    if (!booking?.id) {
      return;
    }

    const fetchFullBooking = options?.fetchFullBooking ?? !booking?.bookingCode;
    this.openEditBookingModal(merchant, store, booking, viewDate, fetchFullBooking, options?.modalClass);
  }

  private openEditBookingModal(merchant, store, booking, viewDate?, fetchFullBooking = false, modalClass = 'modal-xl') {
    const initialState = {
      day: format(parse(booking.dateInit, 'yyyy-MM-dd HH:mm', new Date()), 'yyyy-MM-dd'),
      store: store,
      event: booking,
      merchant: merchant,
      fetchFullBooking,
    };
    // initialState['readOnly'] = this.calcDayIsPast(viewDate);

    this.bsModalRef = this.modalService.show(CreateBookingModalComponent, { initialState, class: modalClass });
    this.editModalCallback = this.modalService.onHide.subscribe((reason: string) => {
      if (reason && reason === 'saved') {
        // it's going to be refreshed anyway
        // this.refreshCalendarObservable.next(true);
      }
      if (reason && reason === 'delete') {
        const bookingToDelete = this.bsModalRef.content?.event ?? booking;
        this.deleteBooking(bookingToDelete);
      }
      this.editModalCallback.unsubscribe();
    });
  }

  editHoliday(merchant, store, event, viewDate?) {
    const initialState = {
      day: format(parse(event.data.dateInit, 'yyyy-MM-dd HH:mm', new Date()), 'yyyy-MM-dd'),
      storeSlug: store.slug,
      event: event.data,
      merchant: merchant,
    };

    this.bsModalRef = this.modalService.show(CreateHolidayModalComponent, { initialState });
    const modalCallback = this.modalService.onHide.subscribe((reason: string) => {
      if (reason && reason === 'saved') {
        this.refreshCalendarObservable.next(true);
      }
      if (reason && reason === 'delete') {
        this.deleteHoliday(event.data);
      }
      modalCallback.unsubscribe();
    });
  }

  deleteBooking(event) {
    let modal;
    if (event.isRecurrent) {
      modal = ConfirmDeleteRecurrenceModalComponent;
    } else if (event.multipleBookings && event.multipleBookings.length > 0) {
      modal = ConfirmDeleteMultipleModalComponent;
    } else {
      modal = ConfirmModalComponent;
    }

    const initialState = {
      question: this.translate.instant('GENERAL.areYouSure'),
      showNotifyClientOption: isFuture(parse(event.dateInit, 'yyyy-MM-dd HH:mm', new Date())) ? true : false
    };
    this.deleteModalRef = this.modalService.show(modal, { initialState });
    const modalCallback = this.modalService.onHide.subscribe((reason: string) => {

      const notifyClient = this.deleteModalRef.content?.notifyClient;

      let confirmDelete = false;
      let deleteAll = false;
      if (reason && typeof reason === 'boolean') {
        confirmDelete = true;
      }

      if (typeof reason === 'string') {
        if (reason === 'confirmAll') {
          confirmDelete = true;
          deleteAll = true;
        } else if (reason === 'confirm') {
          confirmDelete = true;
        }
      }

      if (confirmDelete) {
        if (this.deletingBookingIds.has(event.id)) {
          modalCallback.unsubscribe();
          return;
        }
        this.deletingBookingIds.add(event.id);
        this.storeService.deleteBooking(event.id, deleteAll, notifyClient).subscribe({
          next: () => {
            this.deletingBookingIds.delete(event.id);
            this.refreshCalendarObservable.next(true);
          },
          error: () => {
            this.deletingBookingIds.delete(event.id);
          },
        });
      }

      modalCallback.unsubscribe();
    });
  }

  deleteHoliday(event) {
    let modal;
    if (event.isRecurrent) {
      modal = ConfirmDeleteRecurrenceModalComponent;
    } else {
      modal = ConfirmModalComponent;
    }

    const initialState = {
      question: this.translate.instant('GENERAL.areYouSure'),
    };
    this.deleteModalRef = this.modalService.show(modal, { initialState });
    const modalCallback = this.modalService.onHide.subscribe((reason: string) => {
      let confirmDelete = false;
      let deleteAll = false;
      if (reason && typeof reason === 'boolean') {
        confirmDelete = true;
      }

      if (typeof reason === 'string') {
        if (reason === 'confirmAll') {
          confirmDelete = true;
          deleteAll = true;
        } else if (reason === 'confirm') {
          confirmDelete = true;
        }
      }

      if (confirmDelete) {
        if (this.deletingHolidayIds.has(event.id)) {
          modalCallback.unsubscribe();
          return;
        }
        this.deletingHolidayIds.add(event.id);
        this.storeService.deleteHoliday(event.id, deleteAll).subscribe({
          next: () => {
            this.deletingHolidayIds.delete(event.id);
            this.refreshCalendarObservable.next(true);
          },
          error: () => {
            this.deletingHolidayIds.delete(event.id);
          },
        });
      }
      modalCallback.unsubscribe();
    });
  }

  checkIfMobileDevice() {
    // check using touch events
    if (isPlatformBrowser(this.platformId)) {
      return 'ontouchstart' in window;
    }
  }

  createNewInvoice(merchant, store) {
    const initialState = {
      currentStore: store,
      merchant: merchant,
    };

    this.bsModalRef = this.modalService.show(CreateInvoiceModalComponent, { initialState });
  }

  convertEventsToCalendarFormat(events, weekView = false, disableDragOnHolidays = false, disableBookingDrag = false) {
    const isBrowser = isPlatformBrowser(this.platformId);
    let isDesktop = false;
    if (isBrowser) {
      isDesktop = !this.checkIfMobileDevice();
    }
    const convertedEvents = [];
    events.forEach((evt) => {
      // Rooms taken by providers the user cannot see: read-only, no data to show.
      const isRoomOccupancy = evt.type === 'roomOccupancy';

      let color = evt?.data?.service?.color ?? environment.defaultColor;
      if (evt.type === 'holiday' || isRoomOccupancy) {
        color = null; // use css
      } else {
        color = { primary: this.toolsService.adjustColor(color, 40) , secondary: color }
      }

      // Blocks mirrored from Google Calendar are changed in Google, never moved or resized here.
      const isGoogleImport = evt.type === 'holiday' && !!evt?.data?.importedFromGoogle;

      const draggableEnabled = isDesktop
        && !isRoomOccupancy
        && !isGoogleImport
        && (!disableDragOnHolidays || evt.type !== 'holiday')
        && (!disableBookingDrag || evt.type !== 'booking');
      const resizableEnabled = isDesktop
        && !isRoomOccupancy
        && !isGoogleImport
        && (!disableBookingDrag || evt.type !== 'booking');

      const calendarEvent: CalendarEvent = {
        id: evt.data.id,
        start: parse(evt.data.dateInit, 'yyyy-MM-dd HH:mm', new Date()),
        end: parse(evt.data.dateEnd, 'yyyy-MM-dd HH:mm', new Date()),
        title: evt?.data?.service?.name ?? 'Bloqueado', // no need to translate since it's not being used
        // color:  color,
        allDay: false,
        cssClass: `event-${evt.type}`,
        draggable: draggableEnabled,
        resizable: {
          beforeStart: resizableEnabled,
          afterEnd: resizableEnabled,
        },
        meta: evt,
      };
      // dont repead events with same id (prevent repeated multi-day holidays and store holidays)
      if (!convertedEvents.some(v => v.id === evt.data.id)) {
        convertedEvents.push(calendarEvent)
      }
    });

    return convertedEvents;
  }

  generateAvailableTimesForSelectedDay(flexibleHours = true, bookingFrequency, day, store, flexibleHoursAmount, flexibleMinutesAmount, event?) {
    let weekDay;
    if (day) {
      weekDay = format(parse(day, 'yyyy-MM-dd', new Date()), 'E');
    } else {
      weekDay = format(new Date(), 'E');
    }

    const workplan = store.workplan[weekDay.toLowerCase()];

    const times = [];

    if (workplan && workplan?.length > 0) {
      workplan.forEach((slot, index) => {
        const startHour = slot.start?.split(':');
        const endHour = slot.end?.split(':');

        let currentDate = set(new Date(), { hours: startHour[0], minutes: startHour[1] });
        if (flexibleHours && index === 0 && store.allowBookingsBeforeOpenHours) {
          // add on first
          if (isBefore(startOfToday(), sub(new Date(currentDate), { hours: store.allowBookingsBeforeOpenHours }))) {
            currentDate = sub(currentDate, { hours: store.allowBookingsBeforeOpenHours });
          } else {
            currentDate = startOfToday();
          }
        }
        let endDate = set(new Date(), { hours: endHour[0], minutes: endHour[1] });
        if (flexibleHours && workplan?.length - 1 === index) {
          // remove on last

          if (isAfter(set(new Date(), { hours: 23, minutes: 59 }), add(new Date(endDate), { hours: flexibleHoursAmount }))) {
            endDate = add(endDate, { hours: flexibleHoursAmount });
          } else {
            const hoursToRemove = getHours(endDate);
            const minutesToRemove = getMinutes(endDate);
            endDate = add(endDate, { hours: 24 - hoursToRemove });
            endDate = sub(endDate, { minutes: minutesToRemove });
            flexibleMinutesAmount = (24 - hoursToRemove) * 60 - minutesToRemove;
          }
        }

        // union of all intervals so admin can book on hours-off intervals  (example:lunch)
        // end date should be start date of next interval
        if (workplan?.length - 1 !== index) {
          // if not last slot get next slot interval
          const nextSlot = workplan[index + 1];
          const nextSlotStartHour = nextSlot.start?.split(':');
          endDate = set(new Date(), { hours: nextSlotStartHour[0], minutes: nextSlotStartHour[1] });
        }

        while (isBefore(currentDate, endDate)) {
          times.push(format(currentDate, 'HH:mm'));
          currentDate = add(currentDate, { minutes: bookingFrequency });
        }
      });
    } else {
      let startDate = startOfToday();
      const endDate = endOfToday();
      while (isBefore(startDate, endDate)) {
        times.push(format(startDate, 'HH:mm'));
        startDate = add(startDate, { minutes: bookingFrequency });
      }
    }

    // if editing and event hour is not on array then add it
    if (event) {
      const eventInitValue = format(parse(event.dateInit, 'yyyy-MM-dd HH:mm', new Date()), 'HH:mm');
      if (!times.find((t) => t === eventInitValue)) {
        times.push(eventInitValue);
      }
    }

    return times.sort();
  }

  confirmBookingDrag(data, newProvider?, cancelPreview?) {
    const originalEvent = data.event.meta.data;

    const newStart = data.newStart;
    const newEnd = data.newEnd;

    const oldStart = parse(originalEvent.dateInit, 'yyyy-MM-dd HH:mm', new Date());
    const oldEnd = parse(originalEvent.dateEnd, 'yyyy-MM-dd HH:mm', new Date());
    const startChanged = !isSameMinute(newStart, oldStart);
    const endChanged = !isSameMinute(newEnd, oldEnd);

    const type = data.event.meta.type;

    if (startChanged || endChanged) {
      this.showConfirmBookingDragModal(originalEvent, startChanged, oldStart, newStart, oldEnd, newEnd, type, cancelPreview);
    } else {
      cancelPreview();
    }

  }

  private showConfirmBookingDragModal(
    event,
    startChanged,
    oldStart,
    newStart,
    oldEnd,
    newEnd,
    type,
    cancelPreview,
  ) {
    const initialState = {
      event,
      startChanged,
      oldStart,
      newStart,
      oldEnd,
      newEnd,
      type,
    };
    this.bsModalRef = this.modalService.show(ConfirmBookingDragModalComponent, { initialState });
    const modalCallback = this.modalService.onHide.subscribe((reason: string) => {
      if (reason !== 'saved') {
        cancelPreview();
      }
      modalCallback.unsubscribe();
    });
  }
}
